<template>
  <div>
    <v-bottom-sheet persistent v-model="userLicencesDialog">
      <div class="ul-panel">

        <!-- Tab bar -->
        <div class="ul-tabs-bar">
          <div class="ul-tabs">
            <button
              v-for="(n, idx) in tabs"
              :key="`tab-${idx}`"
              class="ul-tab"
              :class="{ 'ul-tab--active': tab === idx }"
              @click="tab = idx"
            >
              {{ n.label }}
              <span v-if="n.canClose" class="ul-tab-close" @click.stop="closeTab(idx, 'devices')">
                <v-icon size="12">mdi-close</v-icon>
              </span>
            </button>
          </div>

          <admin-table-subheader
            v-model="searchUserLicence"
            search
            search-placeholder="Search licence…"
            asset-icon="mdi-account"
            :asset-name="currentUser.name"
            :refresh-button="true"
            :refresh-loading="syncing"
            :close-button="true"
            :primary-button="{ label: 'Assign Licence', icon: 'mdi-plus', click: openAssignLicenceDialog }"
            class="ul-subheader"
            @refresh="syncUserLicences"
            @close="close"
          />
        </div>

        <!-- Tab content -->
        <div class="ul-tab-content">

          <!-- Licence Table tab -->
          <div v-show="tab === 0" class="ul-table-wrap">
            <v-data-table
              :headers="headers"
              :items="userLicences"
              :search="searchUserLicence"
              :loading="syncing"
              loading-text="Loading licences…"
              :items-per-page="20"
              :footer-props="{ 'items-per-page-options': [10, 20, 50, -1] }"
              class="ec-table"
            >
              <template v-slot:[`item.label`]="{ item }">
                <div class="ul-name-cell">
                  <span class="ul-time-left">{{ item.time_left + 'd' }}</span>
                  <span class="ul-licence-name">{{ item.label }}</span>
                </div>
              </template>

              <template v-slot:[`item.is_cancelled`]="{ item }">
                <span v-if="!item.subscriptions.is_canceled" class="u-status-pill u-pill--green">
                  <v-icon size="10" style="margin-right:3px">mdi-check</v-icon>Active
                </span>
                <span v-else class="u-status-pill u-pill--red">
                  <v-icon size="10" style="margin-right:3px">mdi-close</v-icon>Cancelled
                </span>
              </template>

              <template v-slot:[`item.created_at`]="{ item }">
                <span class="u-date">{{ timeParser(item.licence_keys.created_at) }}</span>
              </template>

              <template v-slot:[`item.started`]="{ item }">
                <span class="u-date">{{ timeParser(item.started) }}</span>
              </template>

              <template v-slot:[`item.endtime`]="{ item }">
                <div class="ul-endtime-cell">
                  <span class="u-date">{{ timeParser(item.endtime) }}</span>
                  <button class="ec-icon-btn ec-icon-btn--xs" @click="updateDurationDate(item)" title="Edit">
                    <v-icon size="13">mdi-circle-edit-outline</v-icon>
                  </button>
                </div>
              </template>

              <template v-slot:[`item.limit`]="{ item }">
                <span class="ul-usage">{{ item.counter_usages }} / {{ item.limit }}</span>
              </template>

              <template v-slot:[`item.features`]="{ item }">
                <button class="ul-text-btn ul-text-btn--blue" @click="getFeatureLimit(item.features, item.label)">
                  <v-icon size="12" style="margin-right:3px">mdi-puzzle-outline</v-icon>Features
                </button>
              </template>

              <template v-slot:[`item.generated_licences`]="{ item }">
                <button class="ul-text-btn ul-text-btn--purple" @click="getUserLicenceKeys(item.generated_licences, item.label)">
                  <v-icon size="12" style="margin-right:3px">mdi-key-outline</v-icon>Keys
                </button>
              </template>

              <template v-slot:[`item.subscriptions`]="{ item }">
                <button class="ul-text-btn ul-text-btn--gray" @click="getSubscriptionDetails(item.subscriptions, item)">
                  <v-icon size="12" style="margin-right:3px">mdi-receipt-text-outline</v-icon>Details
                </button>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="ul-actions">
                  <button class="ec-icon-btn ec-icon-btn--primary" title="Upgrade Licence" @click="upgradeLicence(item)">
                    <v-icon size="14">mdi-tag-arrow-up</v-icon>
                  </button>
                  <button class="ec-icon-btn ec-icon-btn--danger" title="Delete Licence"
                          :disabled="item.label === 'Free'"
                          @click="deleteLicence(item)">
                    <v-icon size="14">mdi-delete-outline</v-icon>
                  </button>
                  <button class="ec-icon-btn" title="See Devices"
                          :disabled="item.devices.length < 1"
                          @click="device_sheet(item)">
                    <v-icon size="14">mdi-cellphone-link</v-icon>
                  </button>
                </div>
              </template>
            </v-data-table>
          </div>

          <!-- Device tabs -->
          <div v-for="(n, idx) in tabs" :key="`content-${idx}`" v-show="tab === idx + 1" class="ul-device-wrap">
            <licences-devices
              :userDevices="userDevices[idx]"
              :licenceName="licenceName"
              :index="idx"
              @callSyncUserDevices="syncUserLicences"
            />
          </div>

        </div>
      </div>
    </v-bottom-sheet>

    <confirm-dialog
      :show.sync="confirmDialog.show"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :item-name="confirmDialog.itemName"
      :confirm-text="confirmDialog.confirmText"
      :type="confirmDialog.type"
      :loading="confirmDialog.loading"
      @confirm="handleConfirm"
    />

    <!-- Edit Duration Date dialog -->
    <v-dialog persistent v-model="showEditDurationDateDialog" width="480">
      <div class="ec-dialog">
        <div class="ec-dialog__header">
          <h2 class="ec-dialog__title">Update Renewal Date</h2>
        </div>
        <v-form lazy-validation v-model="form" ref="updateDurationLicenceForm" :disable="loading">
          <div class="ec-dialog__body">
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
              <div class="ec-field">
                <label class="ec-label">Renewal Date</label>
                <v-menu
                  ref="menu"
                  v-model="menu"
                  :close-on-content-click="false"
                  :return-value.sync="getEndDate"
                  transition="scale-transition"
                  offset-y min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="getEndDate"
                      dense outlined hide-details="auto"
                      hint="MM/DD/YYYY format"
                      append-icon="mdi-calendar"
                      v-bind="attrs" v-on="on"
                    />
                  </template>
                  <v-date-picker :allowed-dates="disablePastDates" v-model="getEndDate" no-title scrollable>
                    <v-spacer />
                    <v-btn text color="primary" @click="$refs.menu.save(getEndDate)">OK</v-btn>
                  </v-date-picker>
                </v-menu>
              </div>
              <div class="ec-field">
                <label class="ec-label">Duration (days) *</label>
                <v-text-field
                  v-model="duration"
                  :rules="[rules.required, rules.mustMoreThanZero]"
                  dense outlined hide-details="auto"
                  type="number" class="inputPrice"
                />
              </div>
            </div>
          </div>
          <div class="ec-dialog__footer">
            <button class="ec-btn ec-btn--ghost" type="button" @click="closeUpdateLicenceDuration">Cancel</button>
            <button class="ec-btn ec-btn--primary" type="button"
                    :disabled="!form || loading" @click="updateLicenceDurationDate">
              <v-progress-circular v-if="loading" size="13" width="2" indeterminate style="margin-right:6px" />
              Update
            </button>
          </div>
        </v-form>
      </div>
    </v-dialog>

    <upgrade-user-licence
      @closeUpgradeLicenceUserDialog="closeUpgradeUserLicence"
      :licenceKeys="licenceKeys"
      :showUpgradeLicenceUserDialog="showUpgradeLicenceUserDialog"
      :currentLicence="currentLicence"
    />

    <assign-licence-form
      :licenceKeys="licenceKeys"
      :showAssignLicenceUserDialog="showAssignLicenceUserDialog"
      :selectedUser="currentUser"
      @closeShowAssignLicenceDialog="showAssignLicenceUserDialog = false"
      @callSyncUserLicences="syncUserLicences"
    />

    <licence-key-feature
      @closeLicenceKeyFeature="showLicenceFeaturesDialog = false"
      @callSyncUserLicences="syncUserLicences"
      :showLicenceFeaturesDialog="showLicenceFeaturesDialog"
      :licenceName="licenceName"
      :features="features"
    />

    <generated-licences
      @closeGeneratedKeysDialog="showGeneratedKeysDialog = false"
      :showGeneratedKeysDialog="showGeneratedKeysDialog"
      :licenceName="licenceName"
      :generatedLicenceKeys="generatedLicenceKeys"
    />

    <subscription-details
      @closeSubscriptionDetailsDialog="showSubscriptionDetailsDialog = false"
      :showSubscriptionDetailsDialog="showSubscriptionDetailsDialog"
      :subscriptionDetails="subscriptionDetails"
      :licenceName="licenceName"
      :licenceEndtime="licenceEndtime"
    />

    <device-info-for-delete-licence
      :licenceForDevicesInfo="licenceForDevicesInfo"
      :devicesInfoForDeleteLicence="devicesInfoForDeleteLicence"
      @closeDevicesInfoForDeleteLicence="devicesInfoForDeleteLicence = false"
      @callSyncUserLicences="syncUserLicences"
    />
  </div>
</template>

<script>
import Vue from 'vue'
import converter from '@/mixins/converter'
import _ from 'lodash'
import { apiService } from '../../service/api.service'
import AssignLicenceForm from '../licences/AssignLicenceForm.vue'
import LicenceKeyFeature from '../features/LicenceKeyFeature.vue'
import ConfirmDialog from '../shared/ConfirmDialog.vue'
import GeneratedLicences from '../licences/GeneratedLicences.vue'
import SubscriptionDetails from '../licences/SubscriptionDetails.vue'
import UpgradeUserLicence from '../licences/UpgradeUserLicence.vue'
import DeviceInfoForDeleteLicence from '../licences/DeviceInfoForDeleteLicence.vue'
import LicencesDevices from '../licences/LicencesDevices.vue'
import AdminTableSubheader from '../shared/AdminTableSubheader.vue'
export default Vue.extend({
  components: {
    AssignLicenceForm,
    LicenceKeyFeature,
    ConfirmDialog,
    GeneratedLicences,
    SubscriptionDetails,
    UpgradeUserLicence,
    DeviceInfoForDeleteLicence,
    LicencesDevices,
    AdminTableSubheader
  },
  mixins: [converter],
  props: {
    userLicencesDialog: { type: Boolean, required: true },
    userLicences: { type: Array, required: true },
    currentUser: { type: Object, required: true }
  },
  created () {
    this.initializeGetLicenceKeys()
  },
  data () {
    return {
      getUserLicenceLoading: false,
      syncing: false,
      showUserDevices: false,
      showAssignLicenceUserDialog: false,
      showLicenceFeaturesDialog: false,
      showGeneratedKeysDialog: false,
      showSubscriptionDetailsDialog: false,
      showUpgradeLicenceUserDialog: false,
      showEditDurationDateDialog: false,
      devicesInfoForDeleteLicence: false,
      searchUserLicence: '',
      licenceName: '',
      licenceEndtime: '',
      duration: null,
      getEndDate: null,
      menu: false,
      sheet: false,
      loading: false,
      form: true,
      tab: 0,
      counter: 0,
      currentLicence: {},
      subscriptionDetails: {},
      selectedLicence: {},
      licenceForDevicesInfo: {},
      devicesToBeDeleted: [],
      userLicenceInfoForEdit: {},
      licenceKeys: [],
      userDevices: [],
      features: [],
      generatedLicenceKeys: [],
      confirmDialog: { show: false, title: '', message: '', itemName: '', confirmText: '', type: 'danger', loading: false },
      pendingAction: null,
      tabs: [
        {
          id: null,
          label: 'Licence Table',
          canClose: false
        }
      ],
      headers: [
        { text: 'Name', value: 'label', color: 'red', align: 'left' },
        { text: 'Status', value: 'is_cancelled', align: 'center' },
        { text: 'Started Date', value: 'started', align: 'center' },
        { text: 'Duration Date', value: 'endtime', align: 'center' },
        { text: 'Licence Usages', value: 'limit', align: 'center' },
        { text: 'Features', value: 'features', align: 'center' },
        { text: 'Generated Licences', value: 'generated_licences', align: 'center' },
        { text: 'Subscription Details', value: 'subscriptions', align: 'center' },
        { text: 'Actions', value: 'actions', align: 'center' }
      ],

      rules: {
        required: (v) => !!v || 'This field is required',
        mustMoreThanZero: (v) => {
          if (!isNaN(v) && v > 0) return true
          return 'Number must be more or equal 0'
        }
      }
    }
  },

  watch: {
    tabs (val) {
      this.tab = val.length - 1
    },
    getEndDate: {
      handler (n) {
        this.watchGetEndDate(n)
      }
    },
    userLicences () {
      this.syncing = false
    }
  },

  methods: {
    async initializeGetLicenceKeys () {
      await apiService
        .get('admin-get-licences')
        .then((res) => {
          this.licenceKeys = res.data.data
        })
        .catch((err) => {
          console.log('ERR', err.response)
        })
    },

    device_sheet (item) {
      this.licenceName = item.label
      this.open_sheet(item)
    },

    async open_sheet (item) {
      if (item.length === 0) {
        this.sheet = false
      } else {
        this.showUserDevices = true
        this.sheet = true
        if (item.id && !_.find(this.tabs, { id: item.id })) {
          this.userDevices.splice(item.devices.length + 1, 0, item.devices)
          this.tabs.push({
            id: item.id,
            label: item?.label,
            canClose: true
          })
        } else {
          const index = _.findIndex(
            this.tabs,
            (e) => {
              return e.id === item.id
            },
            0
          )
          this.tab = index
        }
      }
    },

    upgradeLicence (item) {
      this.currentLicence = item
      this.showUpgradeLicenceUserDialog = true
    },

    watchGetEndDate (date) {
      const convertedDate = new Date(date)
      const durationDay = this.calculateExpireTime(convertedDate.getTime())
      this.duration = durationDay
    },

    updateDurationDate (item) {
      this.showEditDurationDateDialog = true
      this.selectedLicence = item

      const convertedDate = new Date(parseInt(item.endtime) * 1000)
      const month = convertedDate.getUTCMonth() + 1
      const day = convertedDate.getUTCDate()
      const year = convertedDate.getUTCFullYear()

      const newdate = year + '-' + month + '-' + day
      this.getEndDate = newdate
    },

    async updateLicenceDurationDate () {
      if (this.$refs.updateDurationLicenceForm.validate()) {
        const updateLicenceDurationPayload = {
          user_licence_id: this.selectedLicence.id,
          duration: parseInt(this.duration)
        }
        await apiService
          .put('admin-edit-user-licence', updateLicenceDurationPayload)
          .then(() => {
            this.closeUpdateLicenceDuration()
            this.syncUserLicences()
          })
          .catch((err) => {
            console.error('Update licence duration failed', err)
          })
      }
    },

    deleteLicence (licence) {
      if (licence.devices.length > 0) {
        this.devicesInfoForDeleteLicence = true
        this.licenceForDevicesInfo = licence
      } else {
        const payload = { user_id: licence.user_id, licence_id: licence.id, client_ids: [] }
        this.pendingAction = async () => {
          await apiService.post('admin-delete-permanently-user-licence', payload)
          this.syncUserLicences()
        }
        this.confirmDialog = {
          show: true,
          title: 'Delete Licence?',
          message: 'The licence will be permanently deleted.',
          itemName: licence.label,
          confirmText: 'Delete',
          type: 'danger',
          loading: false
        }
      }
    },

    async handleConfirm () {
      if (!this.pendingAction) return
      this.confirmDialog.loading = true
      try {
        await this.pendingAction()
        this.confirmDialog.show = false
      } catch (e) {
        console.error(e)
      } finally {
        this.confirmDialog.loading = false
        this.pendingAction = null
      }
    },

    getFeatureLimit (features, licenceName) {
      this.features = features
      this.licenceName = licenceName
      this.showLicenceFeaturesDialog = true
    },

    getUserLicenceKeys (generatedKeys, licenceName) {
      this.generatedLicenceKeys = generatedKeys
      this.licenceName = licenceName
      this.showGeneratedKeysDialog = true
    },

    getSubscriptionDetails (subscription, licence) {
      this.subscriptionDetails = subscription
      this.licenceName = licence.label
      this.licenceEndtime = licence.endtime
      this.showSubscriptionDetailsDialog = true
    },

    closeUpdateLicenceDuration () {
      this.showEditDurationDateDialog = false
      this.$refs.updateDurationLicenceForm.reset()
    },

    closeUpgradeUserLicence () {
      this.$emit('sync-user-licences', this.currentUser.id)
      this.showUpgradeLicenceUserDialog = false
    },

    openAssignLicenceDialog () {
      this.showAssignLicenceUserDialog = true
    },

    syncUserLicences () {
      this.syncing = true
      this.$emit('sync-user-licences', this.currentUser.id)
    },

    disablePastDates (val) {
      return val >= new Date().toISOString().substr(0, 10)
    },

    closeTab (index, include = null) {
      this.tabs.splice(index, 1)
      if (include === 'devices') {
        this.userDevices = this.deleteRow(this.userDevices, index)
      }
    },

    deleteRow (arr, row) {
      arr = arr.slice(0) // make copy
      arr.splice(row - 1, 1)
      return arr
    },

    close () {
      this.$emit('close-userLicence-dialog')
    }
  }
})
</script>

<style scoped>
.ul-panel {
  background: var(--ec-surface);
  border-radius: var(--ec-radius) var(--ec-radius) 0 0;
  height: 800px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Tab bar */
.ul-tabs-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  border-bottom: 1px solid var(--ec-border);
  background: var(--ec-bg);
  min-height: 52px;
  flex-shrink: 0;
}
.ul-tabs {
  display: flex;
  align-items: stretch;
  gap: 2px;
  flex: 1;
  overflow-x: auto;
  scrollbar-width: none;
}
.ul-tabs::-webkit-scrollbar { display: none; }

.ul-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 16px;
  height: 52px;
  font-size: .82rem;
  font-weight: 500;
  color: var(--ec-text-3);
  border: none;
  background: transparent;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
  transition: color .15s, border-color .15s;
}
.ul-tab:hover { color: var(--ec-text-1); }
.ul-tab--active { color: var(--ec-primary); border-bottom-color: var(--ec-primary); font-weight: 600; }

.ul-tab-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  color: var(--ec-text-3);
  transition: background .12s;
}
.ul-tab-close:hover { background: var(--ec-border); color: var(--ec-text-1); }

.ul-subheader {
  flex-shrink: 0;
  border-bottom: none;
  padding: 0 8px;
  min-height: unset;
  background: transparent;
}

/* Content */
.ul-tab-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.ul-table-wrap,
.ul-device-wrap {
  flex: 1;
  overflow: auto;
}

/* Table → global .ec-table in admin.css */

/* Cell helpers */
.ul-name-cell { display: flex; align-items: center; gap: 8px; }
.ul-time-left {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: 20px;
  font-size: .68rem;
  font-weight: 700;
  background: var(--ec-primary-bg);
  color: var(--ec-primary);
  white-space: nowrap;
}
.ul-licence-name { font-size: .82rem; font-weight: 500; }
.ul-endtime-cell { display: flex; align-items: center; gap: 6px; }
.ul-usage { font-size: .8rem; font-family: monospace; }
.ul-actions { display: flex; align-items: center; gap: 4px; }

/* Text action buttons */
.ul-text-btn {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: .72rem;
  font-weight: 600;
  border: 1px solid currentColor;
  cursor: pointer;
  transition: var(--ec-transition);
  background: transparent;
  opacity: .85;
}
.ul-text-btn:hover { opacity: 1; }
.ul-text-btn--blue  { color: var(--ec-primary); border-color: #c7d2fe; background: var(--ec-primary-bg); }
.ul-text-btn--blue:hover  { background: var(--ec-primary); color: #fff; }
.ul-text-btn--purple { color: #7c3aed; border-color: #ddd6fe; background: #f5f3ff; }
.ul-text-btn--purple:hover { background: #7c3aed; color: #fff; }
.ul-text-btn--gray  { color: var(--ec-text-2); border-color: var(--ec-border); background: var(--ec-bg); }
.ul-text-btn--gray:hover  { background: var(--ec-text-2); color: #fff; }

/* ec-icon-btn--danger hover → global admin.css */
.ec-icon-btn--danger { color: var(--ec-danger); }
.ec-icon-btn--xs { width: 22px; height: 22px; }
</style>
